import {
    Component,
    EventEmitter,
    AfterContentChecked,
    ElementRef,
    TemplateRef,
    ViewChild,
    Output,
    Input
} from '@angular/core';
import { getContentEditableCaretCoords, isInputOrTextAreaElement } from 'src/app/shared/utilities/mentions-utils';
import { getCaretCoordinates } from 'src/app/shared/utilities/caret-coords';
/**
 * Angular Mentions. Original code
 * https://github.com/dmacfarlane/angular-mentions
 *
 * Copyright (c) 2016 Dan MacFarlane
 * Updated by Bernini 2024
 */
@Component({
    selector: 'nova-mention-list',
    templateUrl: './mention-list.component.html',
    styleUrls: ['./mention-list.component.scss']
})
export class MentionListComponent implements AfterContentChecked {
    private element;
    @Input()
    labelKey: string;

    @Input()
    itemTemplate: TemplateRef<any>;

    @Output()
    itemClick: EventEmitter<any>;

    @ViewChild('list', { static: true }) list: ElementRef;
    @ViewChild('defaultItemTemplate', { static: true }) defaultItemTemplate: TemplateRef<any>;
    items: any[];
    activeIndex: number;
    hidden: boolean;
    dropUp: boolean;
    styleOff: boolean;

    private coords;
    private offset;
    constructor(element: ElementRef) {
        this.element = element;
        this.labelKey = 'label';
        this.itemClick = new EventEmitter();
        this.items = [];
        this.activeIndex = 0;
        this.hidden = false;
        this.dropUp = false;
        this.styleOff = false;
        this.coords = { top: 0, left: 0 };
        this.offset = 0;
    }

    ngAfterContentChecked() {
        if (!this.itemTemplate) {
            this.itemTemplate = this.defaultItemTemplate;
        }
    }

    // lots of confusion here between relative coordinates and containers
    position(nativeParentElement, iframe = null) {
        if (isInputOrTextAreaElement(nativeParentElement)) {
            // parent elements need to have postition:relative for this to work correctly?
            this.coords = getCaretCoordinates(nativeParentElement, nativeParentElement.selectionStart, null);
            this.coords.top = nativeParentElement.offsetTop + this.coords.top - nativeParentElement.scrollTop;
            this.coords.left = nativeParentElement.offsetLeft + this.coords.left - nativeParentElement.scrollLeft;
            // getCretCoordinates() for text/input elements needs an additional offset to position the list correctly
            this.offset = this.getBlockCursorDimensions(nativeParentElement).height;
        } else if (iframe) {
            const context = { iframe: iframe, parent: iframe.offsetParent };
            this.coords = getContentEditableCaretCoords(context);
        } else {
            const doc = document.documentElement;
            const scrollLeft = (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
            const scrollTop = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
            // bounding rectangles are relative to view, offsets are relative to container?
            const caretRelativeToView = getContentEditableCaretCoords({ iframe: iframe });
            const parentRelativeToContainer = nativeParentElement.getBoundingClientRect();
            this.coords.top =
                caretRelativeToView.top - parentRelativeToContainer.top + nativeParentElement.offsetTop - scrollTop;
            this.coords.left =
                caretRelativeToView.left - parentRelativeToContainer.left + nativeParentElement.offsetLeft - scrollLeft;
        }
        // set the default/inital position
        this.positionElement();
    }

    get activeItem() {
        return this.items[this.activeIndex];
    }

    activateNextItem() {
        // adjust scrollable-menu offset if the next item is out of view
        const listEl = this.list.nativeElement;
        const activeEl = listEl.getElementsByClassName('active').item(0);
        if (activeEl) {
            const nextLiEl = activeEl.nextSibling;
            if (nextLiEl && nextLiEl.nodeName === 'LI') {
                const nextLiRect = nextLiEl.getBoundingClientRect();
                if (nextLiRect.bottom > listEl.getBoundingClientRect().bottom) {
                    listEl.scrollTop = nextLiEl.offsetTop + nextLiRect.height - listEl.clientHeight;
                }
            }
        }
        // select the next item
        this.activeIndex = Math.max(Math.min(this.activeIndex + 1, this.items.length - 1), 0);
    }

    activatePreviousItem() {
        // adjust the scrollable-menu offset if the previous item is out of view
        const listEl = this.list.nativeElement;
        const activeEl = listEl.getElementsByClassName('active').item(0);
        if (activeEl) {
            const prevLiEl = activeEl.previousSibling;
            if (prevLiEl && prevLiEl.nodeName === 'LI') {
                const prevLiRect = prevLiEl.getBoundingClientRect();
                if (prevLiRect.top < listEl.getBoundingClientRect().top) {
                    listEl.scrollTop = prevLiEl.offsetTop;
                }
            }
        }
        // select the previous item
        this.activeIndex = Math.max(Math.min(this.activeIndex - 1, this.items.length - 1), 0);
    }

    // reset for a new mention search
    reset() {
        this.list.nativeElement.scrollTop = 0;
        this.checkBounds();
    }

    // final positioning is done after the list is shown (and the height and width are known)
    // ensure it's in the page bounds
    checkBounds() {
        let left = this.coords.left,
            dropUp = this.dropUp;
        const top = this.coords.top;
        const bounds = this.list.nativeElement.getBoundingClientRect();
        // if off right of page, align right
        if (bounds.left + bounds.width > window.innerWidth) {
            left -= bounds.left + bounds.width - window.innerWidth + 10;
        }
        // if more than half off the bottom of the page, force dropUp
        // if ((bounds.top+bounds.height/2)>window.innerHeight) {
        //   dropUp = true;
        // }
        // if top is off page, disable dropUp
        if (bounds.top < 0) {
            dropUp = false;
        }
        // set the revised/final position
        this.positionElement(left, top, dropUp);
    }

    positionElement(left = this.coords.left, top = this.coords.top, dropUp = this.dropUp) {
        const el = this.element.nativeElement;
        top += dropUp ? 0 : this.offset; // top of list is next line
        el.className = dropUp ? 'dropup' : null;
        el.style.position = 'absolute';
        el.style.left = left + 'px';
        el.style.top = top + 'px';
    }

    getBlockCursorDimensions(nativeParentElement) {
        const parentStyles = window.getComputedStyle(nativeParentElement);
        return {
            height: parseFloat(parentStyles.lineHeight),
            width: parseFloat(parentStyles.fontSize)
        };
    }
}
